import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartArea, ChartItem } from 'chart.js';
import { constants } from '../../constants';
import { Dashboard } from '../../models/dashboard.model';
import { DiscountTierAndValue } from '../../models/discountTierAndValue.model';
import { HistoryChartData } from '../../models/history-chart-data.model';
import { Review } from '../../models/review.model';
import { MomentPipe } from '../../pipes/moment.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
    selector: 'app-history-chart',
    templateUrl: './history-chart.component.html',
    styleUrls: ['./history-chart.component.css']
})
export class HistoryChartComponent implements AfterViewInit, OnChanges {

    private static readonly DAY_IN_MILLIS: number = 86400000;

    @Input() dashboard: Dashboard;

    @Input() set reviews(reviews: Review[]) {
        this.historyChartData = [];
        this._reviews = reviews.reverse();

        if (!reviews || !this.isPeriodDataAvailable()) {
            return;
        }

        const startDate: Date = new Date(this.chartStartDate);
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() - this.historyMonthCount);

        let reviewFound: boolean = false;
        for (let idx: number = 0; idx < this.historyMonthCount + 4; idx++) {
            const date: number = Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1);
            startDate.setMonth(startDate.getMonth() + 1);

            const reviewByMeasurement: Review | undefined = reviews.find((review: Review) => date === review.getMeasurementStartDate());
            if (reviewByMeasurement) {
                reviewFound = true;
            }

            let initialDiscountValue: number | undefined = undefined;
            if (date >= this.dashboard.getRampUpStartDate()! && date - HistoryChartComponent.DAY_IN_MILLIS <= this.dashboard.getInitialDiscountEndDate()!) {
                initialDiscountValue = this.dashboard.getInitialDiscountPercent() || 0;
            }

            this.historyChartData.push({
                x: date,
                isActivePeriod: reviewFound && idx < this.historyMonthCount + 1,
                achievedDiscount: this.getAchievedDiscount(date),
                baselineDiscount: undefined,
                // initialDiscount: reviewFound ? undefined : this.initialDiscount,
                initialDiscount: initialDiscountValue,
                expectedDiscount: undefined
            });
        }
        const firstActive: HistoryChartData | undefined = this.historyChartData.find((historyChartData: HistoryChartData) => historyChartData.isActivePeriod);
        if (firstActive) firstActive.initialDiscount = this.dashboard.getInitialDiscountPercent() || 0;
        if (this.slider) this.slider.nativeElement.value = this.sliderDefaultValue;

        this.reviewSelected.emit(this.getSelectedReview());
    }

    @Input() discountTiersAndValues: DiscountTierAndValue[];

    @Input() locale: string;

    @Input() printable: boolean;

    @Output() reviewSelected: EventEmitter<Review> = new EventEmitter<Review>();

    @ViewChild('slider') slider: ElementRef;

    // good for webpage
    @Input() canvas: { height: number, width: number };
    // good for print
    // canvasPrint: { height: number, width: number } = { height: 277, width: 650 };
    historyChartData: HistoryChartData[];
    sliderBorderWidth: number = 5;
    sliderRange: { min: number, max: number } = { min: 0, max: 1000 };

    private chart: Chart<"line", HistoryChartData[], number>;
    private _reviews: Review[];

    get display(): boolean {
        return !!this._reviews && this.isPeriodDataAvailable() && this.historyChartData.some((val: HistoryChartData) => val.isActivePeriod);
    }

    get chartAreaHeight(): number {
        return this.chart?.chartArea.height;
    }

    get chartAreaLeft(): number {
        return this.chart?.chartArea.left;
    }

    get chartAreaTop(): number {
        return this.chart?.chartArea.top;
    }

    get chartAreaWidth(): number {
        return this.chart?.chartArea.width;
    }

    get chartStartDate(): number {
        return Math.max(this.dashboard.getDiscountEndDate() ?? 0, this.dashboard.getInitialDiscountEndDate() ?? 0);
    }

    get discountPeriodLength(): number {
        return this.dashboard.getDiscountPeriod() ?? this.dashboard.getNextDiscountPeriod() ?? 0;
    }

    get historyMonthCount(): number {
        return this.measurementPeriodLength + 12;
    }

    get measurementPeriodLength(): number {
        return this.dashboard.getMeasurementPeriod() ?? this.dashboard.getNextMeasurementPeriod() ?? 0;
    }

    get monthWidth(): number {
        return this.sliderRange.max / (this.getActivePeriodsCount() - this.measurementPeriodLength - this.discountPeriodLength);
    }

    get sliderContainerLeft(): number {
        return this.getFirstActiveMonthIdx() * this.chartAreaWidth / (this.historyChartData.length - 1) + this.chartAreaLeft;
    }

    get sliderContainerWidth(): number {
        return this.getActivePeriodsCount() * this.chartAreaWidth / (this.historyChartData.length - 1);
    }

    get sliderDefaultValue(): number {
        return this.sliderRange.max;
    }

    get sliderGreyArrowWidth(): number {
        return (this.chart?.chartArea.width / (this.historyMonthCount + 3) * this.measurementPeriodLength) - 450;
    }

    get sliderHeight(): number {
        return this.chartAreaHeight + 15;
    }

    get sliderWidth(): number {
        return this.chartAreaWidth / (this.historyChartData.length - 1) * (this.measurementPeriodLength + this.discountPeriodLength) + 5;
    }

    constructor(
        private momentPipe: MomentPipe,
        private translatePipe: TranslatePipe
    ) { }

    ngAfterViewInit(): void {
        this.createChart();
        if (this.historyChartData.length) {
            this.onChange(this.sliderDefaultValue);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.locale?.currentValue !== changes.locale?.previousValue && !changes.locale?.firstChange) {
            this.createChart();
        }
    }

    onChange(value: number) {
        const idx: number = Math.round(value / this.monthWidth);
        this.slider.nativeElement.value = this.monthWidth * idx;
        const selectedPeriod = this.historyChartData[this.getFirstActiveMonthIdx() + idx].x;
        this.reviewSelected.emit(this.getSelectedReview(selectedPeriod));
    }

    private createChart(): void {
        this.chart?.destroy();

        const chartItemClass = this.printable ? ".printable-history" : ".not-printable-history";
        const chartItem = document.querySelector(`${chartItemClass} .history-chart-container > canvas`) as ChartItem;

        this.chart = new Chart(chartItem, {
            plugins: [{
                id: 'customization',
                beforeDraw: (chart: Chart, args: { cancelable: true }, options: any) => {
                    const chartArea: ChartArea = chart.chartArea;
                    const ctx: CanvasRenderingContext2D = chart.ctx;

                    ctx.fillStyle = constants.LIGHT_GREY_COLOR;
                    ctx.fillRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height);

                    ctx.fillStyle = constants.LIGHT_LIGHT_GREY_COLOR;
                    ctx.fillRect(this.sliderContainerLeft, chartArea.top, this.sliderContainerWidth, chartArea.height);

                    ctx.setLineDash([2]);
                    ctx.strokeStyle = constants.BLACK_COLOR;

                    ctx.beginPath();
                    ctx.moveTo(this.sliderContainerLeft, chartArea.top);
                    ctx.lineTo(this.sliderContainerLeft, chartArea.bottom);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.moveTo(this.sliderContainerLeft + this.sliderContainerWidth, chartArea.top);
                    ctx.lineTo(this.sliderContainerLeft + this.sliderContainerWidth, chartArea.bottom);
                    ctx.stroke();
                }
            }],
            type: 'line',
            data: {
                datasets: [
                    {
                        borderColor: constants.GREY_COLOR,
                        data: this.historyChartData,
                        label: this.translatePipe.transform('D34', this.locale),
                        parsing: {
                            yAxisKey: 'initialDiscount'
                        },
                        stepped: true
                    },
                    {
                        borderColor: constants.YELLOW_COLOR,
                        data: this.historyChartData,
                        label: this.translatePipe.transform('D35', this.locale),
                        parsing: {
                            yAxisKey: 'baselineDiscount'
                        },
                        stepped: true
                    },
                    {
                        borderColor: constants.RED_COLOR,
                        borderWidth: 3,
                        data: this.historyChartData,
                        label: this.translatePipe.transform('D36', this.locale),
                        parsing: {
                            yAxisKey: 'achievedDiscount'
                        },
                        stepped: true
                    },
                    // {
                    //     borderColor: BLACK_COLOR,
                    //     borderDash: [5, 5],
                    //     data: this.historyChartData,
                    //     label: 'Expected discount',
                    //     parsing: {
                    //         yAxisKey: 'expectedDiscount'
                    //     },
                    //     stepped: true
                    // }
                ],
                labels: this.historyChartData.map((historyChartData: HistoryChartData) => historyChartData.x)
            },
            options: {
                elements: {
                    point: {
                        radius: 0
                    }
                },
                plugins: {
                    legend: {
                        align: 'start',
                        labels: {
                            font: {
                                family: 'Delivery'
                            },
                            pointStyle: 'line',
                            boxHeight: 2
                        },
                        position: 'bottom',
                    },
                    datalabels: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        grid: {
                            drawOnChartArea: false
                        },
                        ticks: {
                            callback: (tickValue: any, index: any, ticks: any) => this.getDateLabel(+tickValue),
                            color: constants.GREY_COLOR,
                            font: {
                                family: "Delivery"
                            },
                            padding: 10,
                            labelOffset: 29 - this.measurementPeriodLength
                        },
                        title: {
                            display: true
                        }
                    },
                    y: {
                        max: this.discountTiersAndValues.length ? this.discountTiersAndValues[this.discountTiersAndValues.length - 1].percent + 10 : 100,
                        min: 0,
                        ticks: {
                            callback: (tickValue, index, ticks) => +tickValue % 20 === 0 ? `${tickValue}%` : ``,
                            color: constants.GREY_COLOR,
                            font: {
                                family: "Delivery"
                            },
                            stepSize: 10
                        }
                    }
                }
            }
        });
    }

    private getAchievedDiscount(date: number): number | undefined {
        const review: Review | undefined = this._reviews.find((review: Review) => {
            if (!review.getDiscountStartDate() || !review.getDiscountEndDate()) {
                return undefined;
            }
            const endDate: Date = new Date(review.getDiscountEndDate()!);
            endDate.setMonth(endDate.getMonth() + 1);
            return date >= review.getDiscountStartDate()! && date <= endDate.getTime();
        });
        if (review == undefined) {
            return undefined;
        }
        return review.getDiscountPercent() || 0.5;
    }

    private getActivePeriodsCount(): number {
        return this.historyChartData.filter((historyChartData: HistoryChartData) => historyChartData.isActivePeriod).length;
    }

    private getDateLabel(idx: number): string {
        const dateLabel: string | null = this.momentPipe.transform(this.historyChartData[idx].x, 'MMM');
        return dateLabel && idx < this.historyChartData.length - 1 ? dateLabel : '';
    }

    private getFirstActiveMonthIdx(): number {
        return this.historyChartData.findIndex((historyChartData: HistoryChartData) => historyChartData.isActivePeriod);
    }

    private getSelectedReview(date?: number): Review {
        const reviews: Review[] = date ? this._reviews.filter((review: Review) => date === review.getMeasurementStartDate()) : this._reviews;
        if (reviews.length > 1) {
            return reviews.reduce((prev: Review, curr: Review) => (prev.getReviewDate() > curr.getReviewDate()) ? prev : curr);
        }
        return reviews[0];
    }

    private isPeriodDataAvailable(): boolean {
        return ((this.dashboard?.getMeasurementPeriod() != null && this.dashboard?.getDiscountPeriod() != null) || (this.dashboard?.getNextMeasurementPeriod() != null && this.dashboard?.getNextDiscountPeriod() != null)) &&
            (this.dashboard?.getInitialDiscountEndDate() != null || this.dashboard?.getDiscountEndDate() != null) &&
            this.dashboard?.getRampUpStartDate() != null;
    }

}
